import { z } from 'zod'
import { OptionSchema, FieldNameSchema } from './common'

/**
 * หน้าตาของแต่ละขั้นตอนเก็บเป็น "รายการบล็อกแบบมีชนิด" (JSON) ไม่ใช่ HTML
 *
 * ผลที่ได้: renderer ตัวเดียว (BlockRenderer) เรนเดอร์ทั้งในหน้าออกแบบ, พรีวิวบนกราฟ
 * และเว็บแอปจริง — สิ่งที่เห็นตอนออกแบบจึงตรงกับที่พนักงานเห็น 100%
 * และไม่มีการ sync สองทางระหว่าง HTML กับ data model (ต้นเหตุบั๊กของ GrapesJS เดิม)
 */

const blockId = z.string().min(1)

/** ข้อความมาตรฐานใน popup ตอนเจอว่าตรงกับข้อมูลเก่า — ใช้เมื่อผู้ออกแบบไม่ได้เขียนข้อความเอง */
export const DEFAULT_LOOKUP_MATCH_MESSAGE = 'ร้านนี้เคยสำรวจไปแล้ว — ระบบบันทึกไว้ในงานนี้แล้ว'

/**
 * เงื่อนไขแสดง/ซ่อนบล็อก อ้างอิงคำตอบของฟิลด์อื่น "ในหน้าเดียวกัน"
 * เช่น ช่อง "อื่น ๆ โปรดระบุ" จะโผล่ก็ต่อเมื่อเลือกตัวเลือก "อื่น ๆ" ไว้เท่านั้น
 *
 * เก็บเป็นโครงสร้างข้อมูล ไม่ใช่ข้อความให้ไป parse ตอนรัน — เหตุผลเดียวกับเงื่อนไขของเส้นเชื่อม
 */
export const VISIBILITY_KINDS = ['equals', 'notEquals', 'includes', 'answered', 'notAnswered'] as const
export type VisibilityKind = (typeof VISIBILITY_KINDS)[number]

export const VISIBILITY_KIND_LABELS: Record<VisibilityKind, string> = {
  equals: 'ตอบตรงกับค่านี้',
  notEquals: 'ตอบไม่ตรงกับค่านี้',
  includes: 'เลือกค่านี้ไว้ด้วย (คำถามเลือกหลายข้อ)',
  answered: 'ตอบอะไรก็ได้',
  notAnswered: 'ยังไม่ได้ตอบ',
}

export const VisibleWhenSchema = z
  .strictObject({
    /** ชื่อฟิลด์ต้นทางในหน้าเดียวกัน */
    fieldName: z.string().min(1),
    kind: z.enum(VISIBILITY_KINDS),
    /** ค่าที่ใช้เทียบ — ไม่ใช้กับ answered/notAnswered */
    value: z.string().default(''),
  })
  .nullable()
  .default(null)

export type VisibleWhen = z.infer<typeof VisibleWhenSchema>

/** ฟิลด์ร่วมของทุกบล็อก */
const blockCommon = {
  id: blockId,
  /** null = แสดงเสมอ */
  visibleWhen: VisibleWhenSchema,
}

/** ฟิลด์ร่วมของบล็อกที่รับคำตอบ */
const inputCommon = {
  ...blockCommon,
  fieldName: FieldNameSchema,
  label: z.string().default(''),
  required: z.boolean().default(false),
  /** ห้ามค่าซ้ำกับคำตอบของงานอื่นในแบบสอบถามเดียวกัน (เช่น รหัสสมาชิก) */
  unique: z.boolean().default(false),
  /** เช็คค่าที่ตอบกับชุดข้อมูลอ้างอิงเก่าที่แอดมินนำเข้าไว้ (เช่น ร้านนี้เคยสำรวจไปแล้วปีที่แล้วหรือไม่) */
  checkAgainstLookup: z.boolean().default(false),
  /** ข้อความใน popup ตอนเจอว่าตรงกับข้อมูลเก่า — เว้นว่าง = ใช้ข้อความมาตรฐาน */
  lookupMatchMessage: z.string().default(''),
  /**
   * ชื่อ "กลุ่มสรุป" สำหรับไฟล์ส่งออก — เว้นว่าง = ไม่รวมกลุ่ม
   *
   * ★ ใช้กับผังที่แตกหลายสาย: คำถามเดียวกันในเชิงความหมายถูกถามคนละข้อในแต่ละสาย
   * ผู้ตอบเดินแค่สายเดียว คอลัมน์ของสายอื่นจึงว่างเป็นเรื่องปกติ แต่คนอ่านรีพอร์ท
   * เข้าใจผิดว่าข้อมูลหาย ตั้งชื่อกลุ่มเดียวกันให้ทุกสาย แล้วไฟล์ส่งออกจะมีคอลัมน์
   * รวมให้อีกชุดหนึ่ง โดยหยิบคำตอบจากสายที่ผู้ตอบเดินจริง
   */
  summaryGroup: z.string().max(60).default(''),
  helpText: z.string().default(''),
}

export const BlockSchema = z.discriminatedUnion('type', [
  // ---------- บล็อกแสดงผล ----------
  z.strictObject({
    ...blockCommon,
    type: z.literal('heading'),
    text: z.string().default(''),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
  }),
  z.strictObject({
    ...blockCommon,
    type: z.literal('paragraph'),
    text: z.string().default(''),
  }),
  z.strictObject({
    ...blockCommon,
    type: z.literal('image'),
    /**
     * อ้างรูปด้วย assetId เท่านั้น — ห้ามฝัง data: URL เพราะทำให้ JSON บวมจนชน limit ของ D1
     * ว่างได้ = เพิ่งลากบล็อกมาวางแต่ยังไม่ได้เลือกไฟล์ (ตอนออกแบบจะเห็นเป็นกรอบให้กดอัปโหลด)
     * ปล่อยว่างไว้แล้วจะเผยแพร่ไม่ได้ — completeness เตือนไว้ให้ ไม่ให้หลุดขึ้นระบบจริง
     */
    assetId: z.string().default(''),
    alt: z.string().default(''),
    widthPct: z.number().min(10).max(100).default(100),
  }),
  z.strictObject({ ...blockCommon, type: z.literal('divider') }),
  z.strictObject({
    ...blockCommon,
    type: z.literal('spacer'),
    size: z.enum(['sm', 'md', 'lg']).default('md'),
  }),
  z.strictObject({
    ...blockCommon,
    type: z.literal('infoBox'),
    text: z.string().default(''),
    variant: z.enum(['info', 'warning', 'success']).default('info'),
  }),

  // ---------- บล็อกรับคำตอบ ----------
  z.strictObject({
    ...inputCommon,
    type: z.literal('textInput'),
    placeholder: z.string().default(''),
    inputMode: z.enum(['text', 'number', 'tel']).default('text'),
    maxLength: z.number().int().positive().max(2000).default(200),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('textarea'),
    placeholder: z.string().default(''),
    rows: z.number().int().min(2).max(12).default(4),
    maxLength: z.number().int().positive().max(5000).default(1000),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('select'),
    placeholder: z.string().default('— เลือก —'),
    options: z.array(OptionSchema).min(1),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('radioGroup'),
    options: z.array(OptionSchema).min(1),
    layout: z.enum(['list', 'buttons']).default('list'),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('checkboxGroup'),
    options: z.array(OptionSchema).min(1),
    maxSelect: z.number().int().positive().nullable().default(null),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('cameraCapture'),
    maxPhotos: z.number().int().min(1).max(10).default(1),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('gpsCapture'),
    /** จับพิกัดอัตโนมัติเมื่อเปิดหน้า */
    auto: z.boolean().default(true),
  }),

  // ---------- ที่อยู่ประเทศไทยแบบเลือกต่อกัน (จังหวัด → อำเภอ → ตำบล) ----------
  // ข้อมูลจังหวัด/อำเภอ/ตำบลโหลดแบบ lazy จาก @shared/data/thailandAddress ตอน render จริงเท่านั้น
  // (ไม่ import ตรงนี้ ไม่งั้นจะพ่วงเข้าไปทุก bundle รวมถึง Worker)
  z.strictObject({
    ...inputCommon,
    type: z.literal('provinceSelect'),
    placeholder: z.string().default('— เลือกจังหวัด —'),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('districtSelect'),
    placeholder: z.string().default('— เลือกอำเภอ/เขต —'),
    /** ชื่อฟิลด์ของบล็อก provinceSelect ในหน้าเดียวกันที่ใช้กรองตัวเลือก */
    provinceFieldName: z.string().default(''),
  }),
  z.strictObject({
    ...inputCommon,
    type: z.literal('subdistrictSelect'),
    placeholder: z.string().default('— เลือกตำบล/แขวง —'),
    /** ต้องมีทั้งคู่เพื่อกันชื่ออำเภอซ้ำข้ามจังหวัด (เช่น "เฉลิมพระเกียรติ") */
    provinceFieldName: z.string().default(''),
    districtFieldName: z.string().default(''),
  }),
])

export type Block = z.infer<typeof BlockSchema>
export type BlockType = Block['type']

export const INPUT_BLOCK_TYPES = [
  'textInput',
  'textarea',
  'select',
  'radioGroup',
  'checkboxGroup',
  'cameraCapture',
  'gpsCapture',
  'provinceSelect',
  'districtSelect',
  'subdistrictSelect',
] as const

export type InputBlockType = (typeof INPUT_BLOCK_TYPES)[number]
export type InputBlock = Extract<Block, { type: InputBlockType }>

export function isInputBlock(block: Block): block is InputBlock {
  return (INPUT_BLOCK_TYPES as readonly string[]).includes(block.type)
}

export const PageDesignSchema = z
  .strictObject({
    schemaVersion: z.literal(1).default(1),
    blocks: z.array(BlockSchema).default([]),
  })
  .superRefine((page, ctx) => {
    const seenFields = new Set<string>()
    const seenIds = new Set<string>()
    page.blocks.forEach((block, index) => {
      if (seenIds.has(block.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['blocks', index, 'id'],
          message: `id ของบล็อกซ้ำกัน: ${block.id}`,
        })
      }
      seenIds.add(block.id)

      if (!isInputBlock(block)) return
      if (seenFields.has(block.fieldName)) {
        ctx.addIssue({
          code: 'custom',
          path: ['blocks', index, 'fieldName'],
          message: `ชื่อฟิลด์ซ้ำกันในหน้าเดียวกัน: ${block.fieldName}`,
        })
      }
      seenFields.add(block.fieldName)
    })
  })

export type PageDesign = z.infer<typeof PageDesignSchema>

export const EMPTY_PAGE: PageDesign = { schemaVersion: 1, blocks: [] }
